const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

type TokenGetter = (() => Promise<string | null>) | null;
let tokenGetter: TokenGetter = null;

export function setAuthTokenGetter(getter: TokenGetter) {
  tokenGetter = getter;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(init.headers as Record<string, string> | undefined) };
  if (tokenGetter) {
    const token = await tokenGetter();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  // Multipart upload. Content-Type is set by fetch when FormData is the body;
  // we skip the JSON default that `request` provides.
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    const headers: Record<string, string> = {};
    if (tokenGetter) {
      const token = await tokenGetter();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },
};

export const API_BASE_URL = API_URL;

// Resolve a media id to its backing URL. Accepts both bare ids and full paths.
export function mediaUrl(idOrPath: string): string {
  if (idOrPath.startsWith('http')) return idOrPath;
  if (idOrPath.startsWith('/media/')) return `${API_URL}${idOrPath}`;
  return `${API_URL}/media/${idOrPath}`;
}
